/**
 * New directory in the Data Lake view (S8 Task 8.10). Creates a REAL directory (`hns-create-directory`; main always
 * sends `If-None-Match: *`, NOTES row M9) under the tab's current directory — never an S2 pending folder. The name is
 * validated live by the Task 8.2 path rules; a relative path creates its missing parents (row M9: mkdir -p). An
 * existing name — or a file in the way — is refused inline from main's `DestinationExists`; a lost answer is said to be
 * unknown. Closing while the request is in flight cancels it.
 */
import { useForm, useStore } from '@tanstack/react-form';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import type { HnsLocator, StorageOperationError } from '../../../../../types/azureStorage';
import { useReportStorageDialogGuard } from '../../properties/dialogs/storageDialogStore';
import { hnsCreateDirectory } from '../hnsApi';
import { outcomeOfHnsError, trackHnsAction } from '../hnsActionAnalytics';
import { useHnsPathMutation } from '../useHnsPathMutation';
import type { HnsHostedDialogProps } from './hnsDialogTypes';
import { hnsLabel, newDirectoryTarget } from './moveDialogModel';

const errorText = (error: StorageOperationError, target: HnsLocator): string =>
  error.category === 'ambiguous_outcome' ? `Outcome unknown — the list was refreshed; check whether ${hnsLabel(target)} exists.` : error.message;

export function NewDirectoryDialog({ connectionId, resource, options, onGuardChange, onClose }: HnsHostedDialogProps) {
  const { run, pending, cancel } = useHnsPathMutation();
  const origin = options.origin ?? 'toolbar';
  const form = useForm({
    defaultValues: { name: '', serverError: '' },
    onSubmit: async ({ value }) => {
      const planned = newDirectoryTarget(resource, value.name);
      if (planned.ok === false) return;
      form.setFieldValue('serverError', '');
      const outcome = await run((requestId) => hnsCreateDirectory({ connectionId, target: planned.target, requestId }, { tabId: options.tabId }));
      if (!outcome.started) return;
      if (!outcome.current) { trackHnsAction('create_directory', 'cancelled', origin); return; }
      if (outcome.error) {
        trackHnsAction('create_directory', outcomeOfHnsError(outcome.error), origin);
        form.setFieldValue('serverError', errorText(outcome.error, planned.target));
        return;
      }
      trackHnsAction('create_directory', 'ok', origin);
      options.onDone?.(outcome.value!.resource);
      onClose();
    },
  });
  const name = useStore(form.store, (state) => state.values.name);
  const serverError = useStore(form.store, (state) => state.values.serverError);
  const planned = newDirectoryTarget(resource, name);
  useReportStorageDialogGuard(onGuardChange, name !== '', pending);
  const close = () => { cancel(); onClose(); };
  const reason = pending ? 'The folder is being created' : planned.ok === false ? planned.reason : undefined;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="max-w-md" data-testid="storage-hns-new-directory-dialog">
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
          <DialogDescription>Creates a folder in {hnsLabel(resource)}. A path such as 2026/10 also creates its missing parent folders; an existing file or folder is never replaced.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); void form.handleSubmit(); }} className="grid gap-3" data-testid="storage-hns-new-directory-form">
          <div className="space-y-2">
            <Label htmlFor="storage-hns-new-directory-name">Name</Label>
            <Input spellCheck={false} autoComplete="off" id="storage-hns-new-directory-name" value={name} autoFocus onChange={(event) => { form.setFieldValue('name', event.target.value); form.setFieldValue('serverError', ''); }} data-testid="storage-hns-new-directory-name" />
          </div>
          {name !== '' && planned.ok === false && <p className="text-xs text-destructive" data-testid="storage-hns-new-directory-invalid">{planned.reason}</p>}
          {planned.ok === true && <p className="text-xs text-muted-foreground" data-testid="storage-hns-new-directory-target">Creates {hnsLabel(planned.target)}</p>}
          {serverError && <p className="text-sm text-destructive" data-testid="storage-hns-new-directory-error">{serverError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} data-testid="storage-hns-new-directory-cancel">Cancel</Button>
            <Button type="submit" disabled={!!reason} title={reason} data-testid="storage-hns-new-directory-submit">Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
